import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronRight, Loader2, Wallet } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/**
 * iter-230bj — Vendor-side "Fix My Razorpay Setup" CTA.
 *
 * Polls /api/vendor/razorpay/setup-status and renders one of:
 *   • Hidden (LA already created + active)
 *   • Green confirmation card (profile complete, awaiting admin auto-create)
 *   • Amber action card (profile incomplete — lists missing fields + deep links)
 *
 * Drop-in for LiteDashboard.jsx — self-contained, no parent state needed.
 */
const RazorpaySetupCTA = () => {
  const navigate = useNavigate();
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall('/vendor/razorpay/setup-status', { method: 'GET' });
      if (res && !res.not_applicable) {
        setStatus(res);
      }
      setError(null);
    } catch (e) {
      setError(e?.message || 'Failed to load payout setup status');
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Hidden states: still loading, errored, not applicable, or LA already
  // exists in an "activated" state — no action needed from vendor.
  if (loading) return null;
  if (error || !status) return null;
  if (status.not_applicable) return null;

  const laId = status.linked_account?.id;
  const laStatus = (status.linked_account?.status || '').toLowerCase();
  const isActivated = laId && laStatus === 'activated';
  if (isActivated) return null;  // nothing to nudge

  // STATE A — profile complete, LA not yet created
  if (status.profile_ready && !laId) {
    return (
      <div
        className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-4"
        data-testid="razorpay-setup-cta-ready"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-emerald-900">
              Payout account almost ready
            </p>
            <p className="text-xs text-emerald-700 mt-1">
              Your profile is complete. An admin will activate your Razorpay
              payout account shortly. You'll see payouts credit to your bank
              once activated.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // STATE B — profile incomplete (LA either missing or stuck in `created`)
  if (!status.profile_ready) {
    const steps = status.next_steps || [];
    return (
      <div
        className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-4"
        data-testid="razorpay-setup-cta-incomplete"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              Complete your payout setup
            </p>
            <p className="text-xs text-amber-800 mt-1">
              {steps.length === 1
                ? 'One detail is missing before your payout account can be created:'
                : `${steps.length} details are missing before your payout account can be created:`}
            </p>
            <ul className="mt-3 space-y-1.5">
              {steps.map((step) => (
                <li key={step.field}>
                  <button
                    onClick={() => navigate(step.edit_path)}
                    data-testid={`razorpay-setup-step-${step.field}`}
                    className="w-full flex items-center justify-between text-left px-3 py-2 rounded-lg bg-white hover:bg-amber-100 border border-amber-200 transition-colors"
                  >
                    <span className="text-sm text-amber-900 font-medium">
                      {step.label}
                    </span>
                    <ChevronRight className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
            {!status.has_bank && (
              <p className="text-[11px] text-amber-700 mt-3">
                Tip: After the above, also add your bank account under
                <button
                  onClick={() => navigate('/vendor/bank')}
                  className="underline font-medium ml-1"
                  data-testid="razorpay-setup-bank-link"
                >
                  Bank & Payouts
                </button>{' '}
                so we can credit your earnings directly.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // STATE C — LA exists but is in `created`/`under_review`/`needs_clarification`
  // and profile is complete. Show a soft progress card.
  if (laId && status.profile_ready) {
    const statusLabel = {
      created: 'Awaiting Razorpay verification',
      under_review: 'Under Razorpay review',
      needs_clarification: 'Razorpay needs more info',
      suspended: 'Account suspended',
    }[laStatus] || 'In progress';

    return (
      <div
        className="rounded-xl border border-sky-200 bg-sky-50 p-4 mb-4"
        data-testid="razorpay-setup-cta-pending"
      >
        <div className="flex items-start gap-3">
          <Loader2 className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-sky-900">
              Payout account: {statusLabel}
            </p>
            <p className="text-xs text-sky-700 mt-1">
              Razorpay reviews usually complete within 24–48 hours. We'll notify
              you once payouts are active.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default RazorpaySetupCTA;
